import { useCallback, useEffect, useRef, useState } from 'react';
import {
  KeyboardAvoidingView,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import { Text } from '@/components/Text';
import { AudioPlayer } from '../AudioPlayer';

import Entypo from '@expo/vector-icons/Entypo';
import FontAwesome from '@expo/vector-icons/FontAwesome';

import {
  responsiveHeight,
  responsiveWidth,
  useResponsiveFontSize,
} from '@/components/Util/reactResponsiveDimensions';
import { diffWords } from 'diff';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import colors from '@/config/colors';
import { PressableNeolexon } from '@/components/PressableNeolexon';

export function Diktat({
  textContent,
  textNormalAudioURL,
  textTitle,
  currentExercise,
  skip,
  playbackRate,
  checkProgress,
  uniqueKey,
  isSelf,
}) {
  const textInputRef = useRef(null);
  const [textInputScroll, setTextInputScroll] = useState(false);
  const [state, setState] = useState({
    exerciseState: 'write',
    textInput: ``,
  });
  const { bottom, left, right, top } = useSafeAreaInsets();
  const diffedArray = useRef([]);
  const correctText = textContent.replace(/[\[\]']+/g, '');

  const horizontalSafeAreaInsets = left + right;
  const verticalSafeAreaInsets = top + bottom;

  const responsiveIconSize = useResponsiveFontSize(4);
  const responsiveFontSize = useResponsiveFontSize(2.25);

  const responsiveStyles = StyleSheet.create({
    responsiveText: {
      fontSize: responsiveFontSize,
      color: colors.darkGrey,
      fontWeight: '700',
    },
    bodyContainer: {
      height: responsiveHeight(62) - verticalSafeAreaInsets,
      width: responsiveWidth(98) - horizontalSafeAreaInsets,
    },
    scrollViewContainer: {
      height: responsiveHeight(62) - verticalSafeAreaInsets,
    },
  });
  const handleTextAreaChange = useCallback((newTextInputValue) => {
    setState((prevState) => {
      return {
        ...prevState,
        textInput: newTextInputValue,
      };
    });
  }, []);

  const normalizeQuotationMarks = (text) => {
    return text.replace(/“|”|„|“/g, '"').replace(/‘|’/g, "'");
  };

  const calculateDifference = useCallback(() => {
    let textInput = normalizeQuotationMarks(state.textInput.trim());
    const correctTextNormalized = normalizeQuotationMarks(correctText);
    const diffedArray = [];

    if (textInput.indexOf(textTitle) === 0) {
      // title is exactly at the beginning
      textInput = textInput.slice(textTitle.length);
    }

    const diff = diffWords(textInput, correctTextNormalized);
    diff.forEach(function (part) {
      const { value, added, removed } = part;
      const color = added ? '#FF3A20' : removed ? 'grey' : 'black';
      const textDecoration = removed ? 'line-through' : 'none';
      diffedArray.push({
        color,
        value,
        textDecoration,
      });
    });

    return diffedArray;
  });

  const handleFertig = useCallback(() => {
    diffedArray.current = calculateDifference();
    diffedArray.current.unshift({
      color: 'black',
      textDecoration: 'none',
      value: `${textTitle}\n`,
      isBold: true,
    });
    setState((prevState) => {
      return {
        ...prevState,
        exerciseState: 'compare',
      };
    });
  });

  const onWeiter = () => {
    skip();
    checkProgress(false, true, true);
  };

  const handleBack = useCallback(() => {
    setState((prevState) => {
      return {
        ...prevState,
        exerciseState: 'write',
      };
    });
  }, []);

  useEffect(() => {
    // Reseting state when another data of the same exercise is available (prev exercise skipped or solved)

    setState({
      exerciseState: 'write',
      textInput: ``,
    });
    diffedArray.current = [];
  }, [currentExercise, textTitle]);

  useEffect(() => {
    if (state.exerciseState === 'write') {
      textInputRef.current.focus();
    }
  }, [state.exerciseState]);

  return (
    <>
      {state.exerciseState == 'write' ? (
        <View style={styles.writeContainer}>
          <View style={styles.actionsContainer}>
            <AudioPlayer
              isTracked
              uniqueKey={uniqueKey}
              source={textNormalAudioURL}
              hasMoveBack
              playbackRate={isSelf ? playbackRate : 0.75}
            />
            <PressableNeolexon
              onPress={handleFertig}
              style={styles.fertigContainer}
            >
              <Text style={responsiveStyles.responsiveText}>fertig</Text>
              <Entypo
                name="arrow-bold-right"
                size={responsiveIconSize}
                color={colors.darkGrey}
              />
            </PressableNeolexon>
          </View>

          <KeyboardAvoidingView
            behavior="height"
            keyboardVerticalOffset={100}
            style={styles.textInputContainer}
          >
            <Text style={styles.titleStyle}>{textTitle}</Text>

            <TextInput
              ref={textInputRef}
              multiline
              scrollEnabled
              numberOfLines={1}
              editable={!textInputScroll}
              onPressIn={() => setTextInputScroll(false)}
              onPressOut={() => setTextInputScroll(false)}
              onScroll={(e) => {
                !e.target.isFocused() && setTextInputScroll(true);
              }}
              spellCheck={false}
              value={state.textInput}
              onChangeText={handleTextAreaChange}
              style={{
                fontSize: 32,
                flex: 1,
                textAlignVertical: 'top',
                color: colors.black,
              }}
            />
          </KeyboardAvoidingView>
        </View>
      ) : (
        <View style={styles.resultContainer}>
          <View style={[styles.actionsContainer]}>
            <PressableNeolexon
              onPress={handleBack}
              style={styles.fertigContainer}
            >
              <FontAwesome
                name="pencil-square-o"
                size={responsiveIconSize}
                color="black"
              />
            </PressableNeolexon>
            <PressableNeolexon
              onPress={onWeiter}
              style={styles.fertigContainer}
            >
              <Text style={responsiveStyles.responsiveText}>weiter</Text>
              <Entypo
                name="arrow-bold-right"
                size={responsiveIconSize}
                color={colors.darkGrey}
              />
            </PressableNeolexon>
          </View>
          <View style={[styles.bodyContainer, responsiveStyles.bodyContainer]}>
            <ScrollView
              contentContainerStyle={styles.scrollViewContentContainer}
              style={responsiveStyles.scrollViewContainer}
            >
              <Text
                style={[
                  responsiveStyles.responsiveText,
                  styles.normal,
                  styles.selectNone,
                ]}
              >
                {diffedArray.current.map((item, index) => {
                  const {
                    color,
                    value,
                    textDecoration: textDecorationLine,
                    isBold,
                  } = item;
                  const styleObj = {
                    color,
                    textDecorationLine,
                    fontWeight: isBold ? 'bold' : 'normal',
                  };
                  return (
                    <Text key={index} style={styleObj} isResponsiveText={true}>
                      {value}
                    </Text>
                  );
                })}
              </Text>
            </ScrollView>
          </View>
        </View>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  resultContainer: {
    flex: 1,
    width: '100%',
  },
  writeContainer: {
    flex: 1,
    width: '100%',
  },
  bodyContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.23,
    shadowRadius: 2.62,
    elevation: 4,
    backgroundColor: colors.white,
  },
  scrollViewContentContainer: {
    paddingVertical: 30,
    paddingHorizontal: 40,
  },
  actionsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 32,
    paddingBottom: 16,
    paddingHorizontal: 32,
  },

  textInputContainer: {
    flex: 1,
    paddingBottom: 20,
    backgroundColor: '#fff',
    paddingHorizontal: 20,
  },

  fertigContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  normal: {
    fontWeight: 'normal',
  },
  selectNone: {
    userSelect: 'none',
  },
  titleStyle: {
    fontWeight: '700',
    fontSize: 32,
    paddingBottom: 10, // Add padding to separate from the TextInput
  },
});
